import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrgStore } from '@/stores/orgStore'
import { useAuthStore } from '@/stores/authStore'
import { calibrationApi } from '@/api/clients/calibrationApi'
import type { CalibrationProposal, ClosureOutcome } from '@/api/types'
import { PageHeader, Card, Button, Field, SelectInput, TextInput } from '@/components/common/ui'
import { Badge } from '@/components/common/Badge'
import { QueryState } from '@/components/common/State'
import { RoleGate } from '@/components/common/RoleGate'
import { formatDate } from '@/components/common/format'

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'neutral'> = {
  approved: 'success',
  pending: 'warning',
}

// $-at-risk composition weight components, in vector order (§12.5).
const WEIGHT_LABELS = [
  'Revenue',
  'Downtime',
  'Transaction',
  'SLA',
  'Breach',
  'HNDL',
  'RPO',
  'Regulatory',
]

const BANDS = ['critical', 'high', 'medium', 'low', 'info']
const OUTCOMES = ['breach', 'no_breach', 'safe_migrated', 'partial_remediation', 'abandoned']

export default function Calibration() {
  const { currentOrgId: orgId } = useOrgStore()
  const userEmail = useAuthStore((s) => s.userEmail) ?? 'reviewer'
  const qc = useQueryClient()

  const proposals = useQuery({
    queryKey: ['calibration-proposals', orgId],
    queryFn: () => calibrationApi.proposals(orgId),
    enabled: !!orgId,
  })
  const approve = useMutation({
    mutationFn: (id: string) => calibrationApi.approve(orgId, id, userEmail),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calibration-proposals', orgId] }),
  })

  return (
    <div>
      <PageHeader
        title="Back-Calibration"
        subtitle="M3 closure outcomes → weight-tuning proposals (human-reviewed, P5)"
      />

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Card className="!p-0">
            <QueryState
              isLoading={proposals.isLoading}
              error={proposals.error}
              isEmpty={proposals.data?.length === 0}
              emptyProps={{
                icon: 'tune',
                title: 'No tuning proposals',
                hint: 'Record closure outcomes (right); a proposal is generated once ≥5 outcomes exist.',
              }}
            >
              <ul className="divide-y divide-border-light dark:divide-border-dark">
                {proposals.data?.map((p) => (
                  <ProposalRow
                    key={p.id}
                    p={p}
                    approving={approve.isPending}
                    onApprove={() => approve.mutate(p.id)}
                  />
                ))}
              </ul>
            </QueryState>
          </Card>
        </div>

        <RoleGate required="security_owner">
          <ClosureForm orgId={orgId} reviewedBy={userEmail} onDone={() =>
            qc.invalidateQueries({ queryKey: ['calibration-proposals', orgId] })
          } />
        </RoleGate>
      </div>
    </div>
  )
}

function ProposalRow({
  p,
  approving,
  onApprove,
}: {
  p: CalibrationProposal
  approving: boolean
  onApprove: () => void
}) {
  return (
    <li className="px-5 py-4">
      <div className="flex items-center gap-3">
        <span className="material-icons text-subtle-light dark:text-subtle-dark">tune</span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-text-light dark:text-text-dark">
            {p.rationale || `Proposal ${p.id.slice(0, 8)}`}
          </p>
          <p className="text-xs text-subtle-light dark:text-subtle-dark">
            {p.sample_size != null ? `${p.sample_size} outcomes` : ''}
            {p.created_at ? ` · ${formatDate(p.created_at)}` : ''}
            {p.reviewed_by ? ` · approved by ${p.reviewed_by}` : ''}
          </p>
        </div>
        <Badge label={p.status} variant={STATUS_VARIANT[p.status] ?? 'neutral'} />
        {p.status === 'pending' && (
          <RoleGate required="security_owner">
            <Button variant="ghost" icon="check" disabled={approving} onClick={onApprove}>
              Approve
            </Button>
          </RoleGate>
        )}
      </div>
      {p.current_weights && p.proposed_weights && (
        <WeightDiff current={p.current_weights} proposed={p.proposed_weights} />
      )}
    </li>
  )
}

// WeightDiff shows the 8 composition weights current → proposed with deltas.
function WeightDiff({ current, proposed }: { current: number[]; proposed: number[] }) {
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border-light dark:border-border-dark">
      <table className="w-full text-xs">
        <thead className="bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark">
          <tr>
            <th className="px-2 py-1.5 text-left font-medium">Component</th>
            <th className="px-2 py-1.5 text-right font-medium">Current</th>
            <th className="px-2 py-1.5 text-right font-medium">Proposed</th>
            <th className="px-2 py-1.5 text-right font-medium">Δ</th>
          </tr>
        </thead>
        <tbody>
          {WEIGHT_LABELS.map((label, i) => {
            const cur = current[i] ?? 0
            const prop = proposed[i] ?? 0
            const delta = prop - cur
            const deltaColor =
              Math.abs(delta) < 1e-6
                ? 'text-subtle-light dark:text-subtle-dark'
                : delta > 0
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-error'
            return (
              <tr
                key={label}
                className="border-t border-border-light dark:border-border-dark text-text-light dark:text-text-dark"
              >
                <td className="px-2 py-1">{label}</td>
                <td className="px-2 py-1 text-right font-mono">{(cur * 100).toFixed(1)}%</td>
                <td className="px-2 py-1 text-right font-mono">{(prop * 100).toFixed(1)}%</td>
                <td className={`px-2 py-1 text-right font-mono ${deltaColor}`}>
                  {delta >= 0 ? '+' : ''}
                  {(delta * 100).toFixed(1)}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ClosureForm records an M3 remediation outcome (POST /back-calibration). Once ≥5
// outcomes exist, the backend emits a weight-tuning proposal for review.
function ClosureForm({
  orgId,
  reviewedBy,
  onDone,
}: {
  orgId: string
  reviewedBy: string
  onDone: () => void
}) {
  const [form, setForm] = useState<ClosureOutcome>({
    predicted_band: 'critical',
    actual_outcome: 'no_breach',
    evidence: '',
    asset_id: '',
  })
  const ingest = useMutation({
    mutationFn: () => {
      const body: ClosureOutcome = {
        predicted_band: form.predicted_band,
        actual_outcome: form.actual_outcome,
        evidence: form.evidence,
        reviewed_by: reviewedBy,
      }
      if (form.asset_id) body.asset_id = form.asset_id
      return calibrationApi.ingestOutcome(orgId, body)
    },
    onSuccess: () => {
      setForm((f) => ({ ...f, evidence: '', asset_id: '' }))
      onDone()
    },
  })

  return (
    <Card title="Record closure outcome">
      <div className="space-y-3">
        <Field label="Predicted band">
          <SelectInput
            value={form.predicted_band}
            onChange={(e) => setForm((f) => ({ ...f, predicted_band: e.target.value }))}
          >
            {BANDS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Actual outcome">
          <SelectInput
            value={form.actual_outcome}
            onChange={(e) => setForm((f) => ({ ...f, actual_outcome: e.target.value }))}
          >
            {OUTCOMES.map((o) => (
              <option key={o} value={o}>
                {o.replace(/_/g, ' ')}
              </option>
            ))}
          </SelectInput>
        </Field>
        <Field label="Asset ID (optional)">
          <TextInput
            value={form.asset_id ?? ''}
            placeholder="uuid"
            onChange={(e) => setForm((f) => ({ ...f, asset_id: e.target.value }))}
          />
        </Field>
        <Field label="Evidence">
          <TextInput
            value={form.evidence ?? ''}
            placeholder="justification for this outcome"
            onChange={(e) => setForm((f) => ({ ...f, evidence: e.target.value }))}
          />
        </Field>
        <Button
          variant="primary"
          icon="add_task"
          disabled={ingest.isPending}
          onClick={() => ingest.mutate()}
        >
          Record outcome
        </Button>
        {ingest.isSuccess && (
          <p className="text-xs text-green-600 dark:text-green-400">
            Outcome recorded — proposal recomputed if ≥5 outcomes exist.
          </p>
        )}
        {ingest.error && (
          <p className="text-xs text-error">Failed to record outcome.</p>
        )}
      </div>
    </Card>
  )
}
